/**
 * Token storage utilities for platform authentication
 *
 * Access token: stored in memory only (security best practice)
 * Refresh token: stored in httpOnly cookie set by backend (XSS protection)
 *
 * The refresh token is never accessible to JavaScript - it's automatically
 * sent with requests to the auth service via credentials: "include"
 */

// Access token stored in memory only - never in localStorage for security
let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
    accessToken = token;
}

export function getAccessToken(): string | null {
    return accessToken;
}

// Clear access token (used during logout)
// Note: Refresh token is in httpOnly cookie, cleared by backend on logout
export function clearTokens(): void {
    accessToken = null;
    // Also clean up any legacy localStorage token from before cookie migration
    try {
        localStorage.removeItem("helmguard_refresh_token");
    } catch {
        // Ignore localStorage errors
    }
}

/**
 * Decode JWT token payload without verification
 * Used for reading expiry time - actual verification happens server-side
 */
export function decodeToken<T = Record<string, unknown>>(token: string): T {
    const parts = token.split(".");
    if (parts.length !== 3) {
        throw new Error("Invalid token format");
    }

    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(decoded) as T;
}

/**
 * Check if a token is expired or about to expire
 * @param token JWT token
 * @param bufferSeconds Seconds before actual expiry to consider as expired (default: 60)
 */
export function isTokenExpired(token: string, bufferSeconds = 60): boolean {
    try {
        const payload = decodeToken<{ exp: number }>(token);
        const expiresAt = payload.exp * 1000; // Convert to milliseconds
        const bufferMs = bufferSeconds * 1000;
        return Date.now() >= expiresAt - bufferMs;
    } catch {
        return true; // Consider invalid tokens as expired
    }
}
